import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Progress, Spin } from 'antd';
import styled from 'styled-components';

import { SkillIcon, TrackIcon } from 'atoms';
import { Colors } from 'context';
import { useProfessions } from 'core';
import { PATHS } from 'router/config';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, mobile, tablet } from 'services/ui';

/**
 * Страница одной профессии (ТЗ 0039, доводка UX).
 *
 * Владелец: «отдельные страницы профессии с call to action, разными
 * информацией: зачем это всё нужно, какие знания нужны для каждой карьеры,
 * насколько ты к ней подходишь».
 *
 * Порядок блоков отвечает ровно на это:
 *   1. чем этот человек занимается (длинный текст, а не строка из списка);
 *   2. НАСКОЛЬКО я подхожу — кольцо и разбивка «есть / не хватает»;
 *   3. каждый навык кликабелен и ведёт на свою страницу;
 *   4. какие НАШИ курсы туда ведут — это и есть призыв к действию.
 *
 * ⚠️ Данные берутся из общего careerProfessions, а не из отдельного запроса
 * на профессию: список короткий (пять-шесть треков), и второй источник тех же
 * чисел разъехался бы с первым.
 */
export const GrowthProfession = memo(() => {
  const { t } = useTranslation();
  const { code = '' } = useParams<{ code: string }>();
  const { professions, loading } = useProfessions();

  const profession = professions.find((row) => row.code === code);

  if (loading && !professions.length) {
    return (
      <PageWrapper>
        <Center>
          <Spin size="large" />
        </Center>
      </PageWrapper>
    );
  }

  if (!profession) {
    return (
      <PageWrapper>
        <BackLink to={PATHS.growthProfessions}>{t('growth.profession.back')}</BackLink>
        <Muted>{t('growth.profession.notFound')}</Muted>
      </PageWrapper>
    );
  }

  const percent = profession.totalCount
    ? Math.round((profession.haveCount / profession.totalCount) * 100)
    : 0;
  const have = profession.skills.filter((skill) => skill.have);
  const missing = profession.skills.filter((skill) => !skill.have);

  return (
    <PageWrapper>
      <BackLink to={PATHS.growthProfessions}>{t('growth.profession.back')}</BackLink>

      <Head>
        <div>
          <Title>
            {/* Тот же знак, что в списке профессий и на карточке трека:
                открыв страницу, студент должен узнать ту самую роль. */}
            <TrackIcon icon={profession.icon} size={36} />
            {profession.title}
          </Title>
          {profession.summary ? <Lead>{profession.summary}</Lead> : null}
        </div>
        <FitBox>
          <Progress
            type="circle"
            size={92}
            percent={percent}
            strokeColor={Colors.signal_green}
            format={() => `${profession.haveCount}/${profession.totalCount}`}
          />
          <Muted>{t('growth.profession.fit')}</Muted>
        </FitBox>
      </Head>

      {profession.about ? <About>{profession.about}</About> : null}

      <Facts>
        {profession.salaryFrom && profession.salaryTo ? (
          <Fact>
            {t('growth.professions.salary', {
              from: profession.salaryFrom,
              to: profession.salaryTo,
              currency: profession.salaryCurrency,
            })}
          </Fact>
        ) : null}
        {/* «В нашей ленте», а не «на рынке»: доска, набитая питоновскими
            вакансиями, по построению объявит Python самой востребованной
            профессией. */}
        <Fact>{t('growth.professions.vacancies', { n: profession.liveVacancies })}</Fact>
      </Facts>

      <Columns>
        <Block>
          <BlockTitle>{t('growth.profession.have')}</BlockTitle>
          {have.length ? (
            <Chips>
              {have.map((skill) => (
                <SkillChip key={skill.id} to={PATHS.growthSkill.replace(':slug', skill.slug)} have>
                  <SkillIcon
                    slug={skill.slug}
                    name={skill.name}
                    category={skill.category}
                    iconUrl={skill.iconUrl}
                    size={18}
                  />
                  {skill.name}
                </SkillChip>
              ))}
            </Chips>
          ) : (
            <Muted>{t('growth.profession.haveNone')}</Muted>
          )}
        </Block>

        <Block>
          <BlockTitle>{t('growth.profession.missing')}</BlockTitle>
          {missing.length ? (
            <Chips>
              {missing.map((skill) => (
                <SkillChip key={skill.id} to={PATHS.growthSkill.replace(':slug', skill.slug)}>
                  <SkillIcon
                    slug={skill.slug}
                    name={skill.name}
                    category={skill.category}
                    iconUrl={skill.iconUrl}
                    size={18}
                  />
                  {skill.name}
                </SkillChip>
              ))}
            </Chips>
          ) : (
            <Muted>{t('growth.profession.missingNone')}</Muted>
          )}
        </Block>
      </Columns>

      {/* Призыв к действию: без него страница — энциклопедия. */}
      <CtaBlock>
        <BlockTitle>{t('growth.professions.ourCourses')}</BlockTitle>
        {profession.courses.length ? (
          <>
            <Muted>{t('growth.profession.coursesHint')}</Muted>
            <Courses>
              {profession.courses.map((course) => (
                <CourseCard
                  key={course.courseId}
                  href={course.courseUrl}
                  target="_blank"
                  rel="noopener noreferrer">
                  <CardTitle>{course.courseName}</CardTitle>
                  <Muted>
                    {course.completed
                      ? t('growth.professions.courseDone')
                      : t('growth.profession.courseOpen')}
                  </Muted>
                </CourseCard>
              ))}
            </Courses>
          </>
        ) : (
          <Muted>{t('growth.professions.noCourseYet')}</Muted>
        )}
      </CtaBlock>
    </PageWrapper>
  );
});

const PageWrapper = styled.div`
  width: 100%;
  padding: 32px 0 80px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  ${desktop} {
    padding: 54px 0 104px;
  }
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px 0;
`;

const BackLink = styled(Link)`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Head = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  ${mobile} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const FitBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex: none;
`;

const Title = styled.h1`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  ${fontService.h3()}

  ${tablet} {
    ${fontService.h2()}
  }
`;

const Lead = styled.div`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const About = styled.p`
  margin: 0;
  max-width: 760px;
  ${fontService.text({ type: TextType.medium })}
`;

const Facts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Fact = styled.span`
  padding: 6px 12px;
  border-radius: 999px;
  background: ${Colors.bg_gray};
  ${fontService.text({ type: TextType.small })}
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  ${mobile} {
    grid-template-columns: 1fr;
  }
`;

const Block = styled.section`
  padding: 16px 18px;
  border: 1px solid ${Colors.gray};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CtaBlock = styled(Block)`
  border-color: ${Colors.main};
`;

const BlockTitle = styled.h2`
  margin: 0;
  ${fontService.text({ type: TextType.medium, weight: FontWeight.bold })}
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

/** Навык — ссылка на его страницу: связь «профессия → навык» кликабельна. */
const SkillChip = styled(Link)<{ have?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid ${({ have }) => (have ? Colors.signal_green : Colors.gray)};
  color: ${({ have }) => (have ? Colors.black : Colors.gray_dark)};
  ${fontService.text({ type: TextType.extraSmall })}
`;

const Courses = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;

  ${mobile} {
    grid-template-columns: 1fr;
  }
`;

const CourseCard = styled.a`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border: 1px solid ${Colors.gray};
  border-radius: 10px;
  color: inherit;

  &:hover {
    border-color: ${Colors.main};
  }
`;

const CardTitle = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const Muted = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall })}
`;
